import { Button } from '@ai-tutor/ui';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  useCreateSubscription,
  useInvalidateBilling,
  usePlans,
  useRefreshSubscription,
} from '@/hooks/account/useBilling';
import type { Plan } from '@/lib/api-client';
import {
  effectiveFirstChargeCents,
  firstRenewalDate,
  formatUsd,
  recurringChargeTerms,
} from '@/lib/billing';
import { track } from '@/config/observability';
import { notify } from '@/lib/notify';

import { UpgradePicker } from '@/components/features/billing/forms/UpgradePicker';
import { StripeProvider } from '@/components/features/billing/StripeProvider';

/**
 * Embedded checkout for the paid plan. Rendered in the "Upgrade" modal from
 * Settings, or inline in onboarding (with a preselected plan).
 *
 * Flow (Stripe's deferred-intent subscription integration):
 *   1. User picks a plan (current Free shown alongside the paid options).
 *   2. Elements are created for that plan's first charge - no intent yet -
 *      so Apple Pay / Google Pay show the real amount. (The old SetupIntent
 *      checkout showed "USD 0.00" on the wallet sheet.)
 *   3. Pay → ``elements.submit()`` validates the form / opens the wallet.
 *   4. We POST /payments/subscriptions; it returns the first invoice's
 *      PaymentIntent client_secret.
 *   5. ``stripe.confirmPayment`` charges the card (3DS inline). A decline
 *      leaves the subscription incomplete; paying again reuses it.
 *   6. Webhook syncs the Subscription doc; we exit on success.
 */
export interface CheckoutFlowProps {
  /** Optional plan id to preselect (skips the interval picker). */
  initialPlanId?: string;
  onBack: () => void;
  /** Called once paid, with the plan bought (onboarding confirms it). */
  onSuccess: (plan: Plan) => void;
  /**
   * Stretch to fill the parent's height and pin the action row to the bottom.
   * Used inside the fixed-height Settings "Upgrade" modal so both the plan
   * picker and the payment step occupy the same height. Off for onboarding
   * (renders inline at its natural height).
   */
  fill?: boolean;
}

export function CheckoutFlow({
  initialPlanId,
  onBack,
  onSuccess,
  fill = false,
}: CheckoutFlowProps) {
  const plansQuery = usePlans();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(
    initialPlanId ?? null
  );
  // The last plan Continue was pressed on, so Back from payment returns to
  // the picker on the same choice.
  const [lastPickedId, setLastPickedId] = useState<string | null>(null);

  const selectedPlan = useMemo<Plan | undefined>(
    () => (plansQuery.data ?? []).find(p => p.id === selectedPlanId),
    [plansQuery.data, selectedPlanId]
  );

  // ── Stage 1: plan picker ──────────────────────────────────────────
  // Skipped when a plan is preselected (onboarding passes initialPlanId).
  if (!selectedPlanId) {
    return (
      <UpgradePicker
        plans={plansQuery.data ?? []}
        loading={plansQuery.isLoading}
        initialPlanId={lastPickedId}
        fill={fill}
        onBack={onBack}
        onContinue={id => {
          setLastPickedId(id);
          setSelectedPlanId(id);
        }}
      />
    );
  }

  // ── Stage 2: payment ─────────────────────────────────────────────
  // Waits only for the plan catalog (a preselected plan can arrive first).
  if (!selectedPlan) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-border ${
          fill ? 'h-full' : 'p-10'
        }`}
      >
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <StripeProvider
      // Elements fix their mode/amount when mounted: a new plan, new Elements.
      key={selectedPlan.id}
      subscription={{
        amountCents: effectiveFirstChargeCents(selectedPlan),
        currency: selectedPlan.currency,
      }}
    >
      <CheckoutInner
        plan={selectedPlan}
        fill={fill}
        onBack={() => {
          if (initialPlanId) {
            // Interval picker was skipped (a plan was preselected), so Back
            // exits the whole checkout to the parent (e.g. the pricing step)
            // instead of re-preselecting the same plan and reloading.
            onBack();
          } else {
            // Return to the interval picker (Settings upgrade flow).
            setSelectedPlanId(null);
          }
        }}
        onSuccess={onSuccess}
      />
    </StripeProvider>
  );
}

// ───────────────────────────────────────────────────────────────────
// Inner checkout (lives INSIDE StripeProvider so it can use Elements)
// ───────────────────────────────────────────────────────────────────

/**
 * Pay: validate the form (or open the wallet sheet), create the subscription,
 * then confirm its first payment against the same Elements. Every failure
 * lands in `errorMsg` beside Pay, and pressing Pay again retries the whole
 * sequence - the backend hands back the still-unpaid subscription.
 */
function useSubscribeAndPay(plan: Plan, onSuccess: (plan: Plan) => void) {
  const stripe = useStripe();
  const elements = useElements();
  const createSubscription = useCreateSubscription();
  const refreshSubscription = useRefreshSubscription();
  const invalidateBilling = useInvalidateBilling();
  // One id per checkout: a double-submitted Pay creates one subscription.
  const [checkoutId] = useState(() => crypto.randomUUID());

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  /** Steps 1-3; resolves to the message to show, or null once paid. */
  const pay = async (stripeJs: Stripe, els: StripeElements) => {
    // Step 1 - validate the card fields / collect the wallet payment. Must
    // run before any await on our own API (Apple Pay needs the user gesture).
    const submitted = await els.submit();
    if (submitted.error) {
      return submitted.error.message || 'Check your card details.';
    }

    // Step 2 - create the subscription; its first invoice is left open for us.
    let clientSecret: string | null;
    try {
      ({ client_secret: clientSecret } = await createSubscription.mutateAsync({
        plan_id: plan.id,
        checkout_id: checkoutId,
      }));
    } catch (err) {
      return err instanceof Error
        ? err.message
        : 'Could not create subscription.';
    }

    // Step 3 - charge the card against that invoice (3DS inline). No secret
    // means nothing is due today, so there is nothing to confirm.
    if (!clientSecret) return null;
    const payResult = await stripeJs.confirmPayment({
      elements: els,
      clientSecret,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    return payResult.error
      ? payResult.error.message || 'Payment confirmation failed.'
      : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorMsg(null);
    const failure = await pay(stripe, elements);
    if (failure) {
      track('checkout_failed', { plan_id: plan.id });
      setErrorMsg(failure);
      setSubmitting(false);
      return;
    }
    track('checkout_completed', {
      plan_id: plan.id,
      interval: plan.interval,
      amount_cents: plan.amount_cents,
      currency: plan.currency,
      has_promo: Boolean(plan.promo),
    });

    // Pull the finalized subscription from Stripe into our mirror right now so
    // Settings reflects the paid plan immediately (webhooks can lag or, in
    // local dev, not be forwarded). Best-effort - the poll + webhook still
    // reconcile if this fails.
    try {
      await refreshSubscription.mutateAsync();
    } catch {
      invalidateBilling();
    }

    notify.success('Welcome to ' + plan.name + '!');
    setSubmitting(false);
    onSuccess(plan);
  };

  return {
    ready: !!stripe && !!elements,
    submitting,
    errorMsg,
    clearError: () => setErrorMsg(null),
    handleSubmit,
  };
}

/** Apple Pay's sheet states the renewal too, not just today's charge. */
function applePayRecurring(plan: Plan) {
  const interval = plan.interval === 'year' ? 'year' : 'month';
  const renewal = {
    amount: plan.amount_cents,
    label: `${plan.name} (${interval}ly)`,
    recurringPaymentIntervalUnit: interval,
    recurringPaymentIntervalCount: 1,
  } as const;
  return {
    recurringPaymentRequest: {
      paymentDescription: `Xonera ${plan.name} subscription`,
      managementURL: `${window.location.origin}/settings?tab=payment`,
      regularBilling: plan.promo
        ? { ...renewal, recurringPaymentStartDate: firstRenewalDate(plan) }
        : renewal,
      ...(plan.promo && {
        trialBilling: {
          amount: plan.promo.first_period_amount_cents,
          label: `${plan.name} - first ${interval} (${plan.promo.code})`,
          recurringPaymentEndDate: firstRenewalDate(plan),
        },
      }),
    },
  };
}

function CheckoutInner({
  plan,
  onBack,
  onSuccess,
  fill = false,
}: {
  plan: Plan;
  onBack: () => void;
  onSuccess: (plan: Plan) => void;
  fill?: boolean;
}) {
  const { ready, submitting, errorMsg, clearError, handleSubmit } =
    useSubscribeAndPay(plan, onSuccess);
  // The step renders at once (no intent to fetch first), so Pay waits for
  // Stripe's card iframe: submitting before it loads fails with no field
  // errors to point at.
  const [elementReady, setElementReady] = useState(false);

  return (
    <form
      onSubmit={handleSubmit}
      data-testid='checkout-form'
      className={fill ? 'flex h-full min-h-0 flex-col' : 'flex flex-col'}
    >
      {/* The body scrolls; the error banner and the Pay row are pinned below
          it. In the fixed-height Settings modal a failed submit (Stripe's
          field errors plus the banner) used to push Pay past the modal's
          bottom edge. Inline in onboarding nothing is pinned: the wizard card's
          body is the scroll box, and a sticky footer over its translucent
          surface showed as a pale slab. The banner sits with Pay so the message about
          the last attempt is always beside the button that retries it. */}
      <div
        data-testid='checkout-body'
        className={
          fill ? 'min-h-0 flex-1 space-y-5 overflow-y-auto pr-1' : 'space-y-5'
        }
      >
        <div className='flex items-center justify-between'>
          <div>
            <h3 className='font-semibold text-foreground'>
              Subscribe to {plan.name}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {plan.promo && (
                <span className='mr-1.5 text-muted-foreground line-through'>
                  {formatUsd(plan.amount_cents)}
                </span>
              )}
              {formatUsd(effectiveFirstChargeCents(plan))}{' '}
              {plan.interval === 'year' ? 'first year' : `per ${plan.interval}`}{' '}
              · cancel anytime
            </p>
            {plan.promo && (
              <p className='dark:tint-brand mt-1 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700'>
                <Check className='h-3 w-3' />
                Coupon {plan.promo.code} applied · then{' '}
                {formatUsd(plan.amount_cents)}/yr
              </p>
            )}
          </div>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='max-sm:h-11'
            onClick={onBack}
            disabled={submitting}
          >
            <ArrowLeft className='mr-1 h-3.5 w-3.5' />
            Back
          </Button>
        </div>

        <div className='rounded-lg border border-border p-4'>
          <PaymentElement
            options={{ layout: 'tabs', applePay: applePayRecurring(plan) }}
            onReady={() => setElementReady(true)}
            // A failed submit leaves its message here ("Your card number is
            // incomplete."), and it used to stay beside a card the user had
            // since completed, until they paid again. Clear it the moment the
            // card is complete; Stripe's own field errors cover the in-between.
            onChange={event => {
              if (event.complete) clearError();
            }}
          />
        </div>
      </div>

      <div
        data-testid='checkout-footer'
        className={fill ? 'shrink-0 space-y-3 pt-4' : 'space-y-3 pt-4'}
      >
        {errorMsg && (
          <div
            data-testid='checkout-error'
            role='alert'
            className='rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive-emphasis'
          >
            {errorMsg}
          </div>
        )}
        {/* The recurring commitment sits beside the button that makes it, so
            nobody pays without seeing what renews and when. */}
        <div className='flex items-center justify-between gap-4'>
          <p
            data-testid='checkout-terms'
            className='text-xs text-muted-foreground'
          >
            {recurringChargeTerms(plan)}
          </p>
          <Button
            className='shrink-0'
            data-testid='pay-button'
            type='submit'
            disabled={!ready || !elementReady || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className='mr-1 h-4 w-4 animate-spin' />
                Processing…
              </>
            ) : (
              `Pay ${formatUsd(effectiveFirstChargeCents(plan))}`
            )}
          </Button>
        </div>

        <p className='text-center text-xs text-muted-foreground'>
          Your card is processed securely by Stripe. We never see your card
          details.
        </p>
      </div>
    </form>
  );
}
